/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { buildApiFilters, buildSearchParams, prepareFilters } from "../services/Activities/activityFunctions";
import { useCallback, useEffect, useState } from "react";
import { useCurrentUser } from "../provider/CurrentUserProvider";
import { useBackofficeUser } from "../provider/BackofficeUserProvider";
import useActivitiesFetching from "./useActivitiesFetching";
import { getFullActivitiesSet } from "../services/Activities/activityTypes";
import { apiGet } from "../services/Api/apiCall";

type UseActivitiesWithFiltersOptions = {
  itemsPerPage?: number;
  activeTab: string;
  storageKey: string;
};

const ITEMS_PER_PAGE = 500;

export const useActivityOverviewWithFilters = ({
  itemsPerPage = ITEMS_PER_PAGE,
  activeTab,
  storageKey,
}: UseActivitiesWithFiltersOptions) => {
  const [page, setPage] = useState(1);
  const currentUser = useCurrentUser();
  const { agents, backofficeUsers } = useBackofficeUser();
  const initialFilters = prepareFilters(currentUser, agents, "activityFilters", "all", "open");
  const [productFilter, setProductFilter] = useState(initialFilters.productFilter);
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>(initialFilters.activityTypeFilter);
  const [timespanFilter, setTimespanFilter] = useState(initialFilters.timespanFilter);
  const [subjectFilter, setSubjectFilter] = useState(initialFilters.subjectFilter);
  const [userFilter, setUserFilter] = useState(initialFilters.userFilter);
  const [openOrDoneFilter, setOpenOrDoneFilter] = useState(initialFilters.openOrDoneFilter);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [overdueTotal, setOverdueTotal] = useState<number | null>(null);
  const [todayTotal, setTodayTotal] = useState<number | null>(null);

  const getSearchParams = useCallback(
    ({
      itemsPerPage = ITEMS_PER_PAGE,
      onlyTotal,
      ...buildApiFiltersArgs
    }: {
      itemsPerPage?: number;
      onlyTotal?: boolean;
    } & Parameters<typeof buildSearchParams>[0] = {}) => {
      const apiFilters = buildApiFilters({
        productFilter: productFilter,
        activityTypeFilter: activityTypeFilter,
        subjectFilter: subjectFilter,
        userFilter: userFilter,
        timespanFilter: timespanFilter,
        openOrDoneFilter: openOrDoneFilter,
        backofficeUsers: backofficeUsers,
        ...buildApiFiltersArgs,
      });

      return buildSearchParams({
        ...apiFilters,
        ...(onlyTotal ? { itemsPerPage: 0 } : { page, itemsPerPage }),
        refreshCounter: refreshCounter,
        pagination: true,
      }).toString();
    },
    [
      productFilter,
      activityTypeFilter,
      subjectFilter,
      userFilter,
      timespanFilter,
      openOrDoneFilter,
      backofficeUsers,
      refreshCounter,
      page,
      itemsPerPage,
    ]
  );

  const getCombinedFilter = (tabBaseFilters: string[], globalActivityTypeFilter: string) => {
    if (globalActivityTypeFilter === "all") {
      return tabBaseFilters;
    }

    if (tabBaseFilters.includes(globalActivityTypeFilter)) {
      return [globalActivityTypeFilter];
    }

    // Global filter does not apply to this Filter; force an empty API response.
    return ["NO_MATCH"];
  };

  /*
   * The api calls for every single tab
   */
  const mailAndDocumentsActivitiesFilter = ["mail", "email", "check_document", "message"];
  const mailAndDocumentActivitiesResult = useActivitiesFetching(
    getSearchParams({
      activityTypeFilter: getCombinedFilter(mailAndDocumentsActivitiesFilter, activityTypeFilter),
      ...(timespanFilter === "overdue" ? { openOrDoneFilter: "open" } : {}),
    }),
    backofficeUsers
  );

  const checkCaseActivitiesFilter = ["check_case"];
  const checkCaseActivitiesResult = useActivitiesFetching(
    getSearchParams({
      activityTypeFilter: getCombinedFilter(checkCaseActivitiesFilter, activityTypeFilter),
      ...(timespanFilter === "overdue" ? { openOrDoneFilter: "open" } : {}),
    }),
    backofficeUsers
  );

  // A list of all activity types names excluding checkCaseActivitiesFilter and mailAndDocumentsActivitiesFilter because they are always present in different tabs
  const activitiesFilter = getFullActivitiesSet()
    .filter((activity) => ![...mailAndDocumentsActivitiesFilter, ...checkCaseActivitiesFilter].includes(activity.name))
    .map((activity) => activity.name);
  const activitiesResult = useActivitiesFetching(
    getSearchParams({
      // If activityTypeFilter is all pass in activitiesFilter to exclude the types mentioned there, otherwise just pass in the actual filter and let buildApiFilters handle the logic
      activityTypeFilter: activityTypeFilter === "all" ? activitiesFilter : activityTypeFilter,
      ...(timespanFilter === "overdue" ? { openOrDoneFilter: "open" } : {}),
    }),
    backofficeUsers
  );

  // The total item amount the current selected tab has
  const currentTabTotalItemCount = (() => {
    switch (activeTab) {
      case "checkCases":
        return checkCaseActivitiesResult.totalItems;
      case "inbox":
        return mailAndDocumentActivitiesResult.totalItems;
      case "activities":
        return activitiesResult.totalItems;
    }
  })();

  // The amount of total pages the current selected tab, from the context above has
  const currentTabTotalPages = Math.ceil((currentTabTotalItemCount ?? ITEMS_PER_PAGE) / itemsPerPage);

  // On any filter change set back page to page 1
  useEffect(() => {
    setPage(1);
  }, [
    activeTab,
    productFilter,
    activityTypeFilter,
    subjectFilter,
    userFilter,
    timespanFilter,
    openOrDoneFilter,
    backofficeUsers,
  ]);

  // Updates overdueTotal and todayTotal on userFilter change this is the amount of activities a specific user has e.g. backoffice user 114
  useEffect(() => {
    if (["all", "accountManager", "internalLawyer", "externalLawyer"].includes(userFilter)) {
      setOverdueTotal(null);
      setTodayTotal(null);
      return;
    }

    apiGet(
      "activities?" +
        getSearchParams({
          onlyTotal: true,
          timespanFilter: "overdue",
          openOrDoneFilter: "open",
        })
    ).then((result) => {
      setOverdueTotal(result["hydra:totalItems"]);
    });

    apiGet(
      "activities?" +
        getSearchParams({
          onlyTotal: true,
          timespanFilter: "today",
        })
    ).then((result) => {
      setTodayTotal(result["hydra:totalItems"]);
    });
  }, [userFilter, getSearchParams]);

  const updateActivities = () => {
    setRefreshCounter(refreshCounter + 1);
  };

  useEffect(() => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        productFilter: productFilter,
        activityTypeFilter: activityTypeFilter,
        userFilter: userFilter,
        timespanFilter: timespanFilter,
        openOrDoneFilter: openOrDoneFilter,
      })
    );
  }, [storageKey, productFilter, activityTypeFilter, userFilter, timespanFilter, openOrDoneFilter]);

  return {
    productFilter,
    setProductFilter,
    activityTypeFilter,
    setActivityTypeFilter,
    timespanFilter,
    setTimespanFilter,
    subjectFilter,
    setSubjectFilter,
    userFilter,
    setUserFilter,
    openOrDoneFilter,
    setOpenOrDoneFilter,
    activitiesResult,
    mailAndDocumentActivitiesResult,
    checkCaseActivitiesResult,
    currentTabTotalPages,
    page,
    setPage,
    updateActivities,
    refreshCounter,
    setRefreshCounter,
    overdueTotal,
    todayTotal,
  };
};
